import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  Banknote,
  Building2,
  CheckCircle2,
  FileCheck2,
  Lock,
  Scale,
  Store,
  UserSquare2,
  UtensilsCrossed,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import type { ZodIssue } from 'zod';

import FormCheckbox from 'shared/components/forms/FormCheckbox';
import { useLanguage } from 'shared/context/LanguageContext';
import { afterPaint, scrollToFirstInvalidField } from 'shared/lib/scroll';
import ReviewSection, {
  type ReviewItem,
  type SectionStatus,
} from 'features/onboarding/components/review/ReviewSection';
import FilePreviewModal, {
  type FilePreviewItem,
} from 'features/onboarding/components/review/FilePreviewModal';
import { useOnboarding } from 'features/onboarding/context/OnboardingContext';
import { useOnboardingOptions } from 'features/onboarding/hooks/useOnboardingOptions';
import { submitOnboarding } from 'features/onboarding/api/integrations';
import { checkDuplicate } from 'features/onboarding/api/checkTaxId';
import { isCompanyTaxId } from 'features/onboarding/schemas/primitives';
import { logger } from 'shared/lib/logger';
import AlreadyRegisteredModal from 'features/onboarding/components/AlreadyRegisteredModal';
import ReviewHoursCard from 'features/onboarding/components/review/ReviewHoursCard';
import {
  storeTypeKey,
  type DocumentKey,
  type FileMeta,
} from 'features/onboarding/types/onboarding.types';
import {
  bankAccountSchema,
  hoursScheduleSchema,
  representativeSchema,
  stepBusinessSchema,
  stepContactSchema,
  stepMenuSchema,
  taxLegalSchema,
} from 'features/onboarding/schemas';

const renderYesNo = (value: boolean | null, yes: string, no: string): string => {
  if (value === null) return '';
  return value ? yes : no;
};

const sectionAnchor = (key: string): string => `review-section-${key}`;

const SECTION_KEYS = [
  'contact',
  'business',
  'tax',
  'bank',
  'representative',
  'menu',
  'documents',
] as const;

type ReviewSectionKey = (typeof SECTION_KEYS)[number] | 'hours';

interface ReviewValidationIssue {
  key: string;
  section: ReviewSectionKey;
  label: string;
  message: string;
}

const Step8Review: React.FC = () => {
  const { t } = useTranslation();
  const { dir } = useLanguage();
  const { data, setStep, setMetadata, updateSection, goBack, goNext } = useOnboarding();
  const { data: options } = useOnboardingOptions();

  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | undefined>();
  const [alreadyRegisteredOpen, setAlreadyRegisteredOpen] = useState(false);
  const [showHoursErrors, setShowHoursErrors] = useState(false);
  const [validationIssues, setValidationIssues] = useState<ReviewValidationIssue[]>([]);

  const fieldErrors = useMemo(
    () =>
      validationIssues.reduce<Record<string, string>>((errors, issue) => {
        errors[issue.key] = issue.message;
        return errors;
      }, {}),
    [validationIssues],
  );

  // Lightbox state for clicking a document/image preview.
  const [previewItems, setPreviewItems] = useState<FilePreviewItem[]>([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);

  const isSubmitted =
    data.metadata.status === 'Submitted' || Boolean(data.metadata.submissionId);

  const NextIcon = dir === 'rtl' ? ArrowLeft : ArrowRight;
  const yes = t('common.yes') as string;
  const no = t('common.no') as string;

  const bankOptions = useMemo(
    () => (options?.banks ?? []).map((o) => ({ value: o.label, label: o.labelClean })),
    [options],
  );

  const storeTypeLabel = data.businessInfo.storeType
    ? (t(`storeTypes.${storeTypeKey(data.businessInfo.storeType)}`, {
        defaultValue: data.businessInfo.storeType,
      }) as string)
    : '';

  const categoryLabel = data.businessInfo.businessCategory
    ? (t(`businessCategory.options.${data.businessInfo.businessCategory}`) as string)
    : '';

  const deliveryCompaniesValue = useMemo(() => {
    const labels = data.businessQuestions.deliveryCompanies.map((c) =>
      c === 'other'
        ? data.businessQuestions.otherDeliveryCompanies.trim()
        : (t(`businessQuestions.deliveryCompanyOptions.${c}`) as string),
    );
    return labels.filter(Boolean).join(', ');
  }, [data.businessQuestions, t]);

  const kosherCertificationsValue = useMemo(
    () =>
      data.businessQuestions.kosherCertifications
        .map((c) => t(`businessQuestions.kosherCertifications.options.${c}`) as string)
        .filter(Boolean)
        .join(', '),
    [data.businessQuestions.kosherCertifications, t],
  );

  const menuSourceValue = useMemo(() => {
    const source = data.businessQuestions.menuSource;
    if (!source) return '';
    const label = t(`businessQuestions.menuSource.options.${source}`) as string;
    if (source === 'website' && data.businessQuestions.menuWebsiteUrl?.trim()) {
      return `${label} — ${data.businessQuestions.menuWebsiteUrl.trim()}`;
    }
    if (source === 'upload' && data.businessQuestions.menuFile) {
      return `${label} — ${data.businessQuestions.menuFile.name}`;
    }
    if (source === 'pos' && data.businessQuestions.posType?.trim()) {
      const posLabel = `${label} — ${data.businessQuestions.posType.trim()}`;
      return data.businessQuestions.menuFile
        ? `${posLabel} — ${data.businessQuestions.menuFile.name}`
        : posLabel;
    }
    return label;
  }, [data.businessQuestions, t]);

  // Section "contact" — trade name, contact person, mobile, pinned address (step 2).
  const contactItems: ReviewItem[] = useMemo(
    () => [
      { label: t('businessInfo.commercialName') as string, value: data.businessInfo.commercialName, error: fieldErrors['contact.commercialName'] },
      { label: t('businessInfo.contactPerson') as string, value: data.businessInfo.ownerName, error: fieldErrors['contact.ownerName'] },
      { label: t('businessInfo.phoneNumber') as string, value: data.businessInfo.phoneNumber, error: fieldErrors['contact.phoneNumber'] },
      { label: t('businessInfo.address') as string, value: data.businessInfo.address },
      { label: t('businessInfo.location') as string, value: data.businessInfo.location, error: fieldErrors['contact.latitude'] },
    ],
    [data.businessInfo, fieldErrors, t],
  );

  // Section "business" — what the business is, how it operates, and its
  // remaining contact channels and schedule (step 3).
  const businessItems: ReviewItem[] = useMemo(
    () => [
      { label: t('businessCategory.question') as string, value: categoryLabel, error: fieldErrors['business.businessCategory'] },
      { label: t('businessInfo.storeType') as string, value: storeTypeLabel, error: fieldErrors['business.storeType'] ?? fieldErrors['business.storeTypeOther'] },
      {
        label: t('businessQuestions.isKosher') as string,
        value: renderYesNo(data.businessQuestions.isKosher, yes, no),
      },
      ...(data.businessQuestions.isKosher
        ? [
            {
              label: t('businessQuestions.kosherCertifications.label') as string,
              value: kosherCertificationsValue,
            },
          ]
        : []),
      {
        label: t('businessQuestions.worksWithOtherDeliveryCompanies') as string,
        value: deliveryCompaniesValue,
        error: fieldErrors['business.otherDeliveryCompanies'],
      },
      { label: t('businessInfo.businessPhoneNumber') as string, value: data.businessInfo.businessPhoneNumber, error: fieldErrors['business.businessPhoneNumber'] },
      { label: t('businessInfo.email') as string, value: data.businessInfo.email, error: fieldErrors['business.email'] },
    ],
    [categoryLabel, storeTypeLabel, data.businessInfo, data.businessQuestions, deliveryCompaniesValue, fieldErrors, kosherCertificationsValue, yes, no, t],
  );

  // Section "menu" — menu source + photo shoot + logo (step 7).
  const menuItems: ReviewItem[] = useMemo(
    () => [
      {
        label: t('businessQuestions.menuSource.question') as string,
        value: menuSourceValue,
        error: fieldErrors['menu.menuSource'] ?? fieldErrors['menu.menuWebsiteUrl'] ?? fieldErrors['menu.menuFile'] ?? fieldErrors['menu.posType'],
      },
      {
        label: t('businessQuestions.wantsMenuPhotoShoot') as string,
        value: renderYesNo(data.businessQuestions.wantsMenuPhotoShoot, yes, no),
        error: fieldErrors['menu.wantsMenuPhotoShoot'],
      },
      {
        label: t('documents.businessLogo') as string,
        value: data.documents.businessLogo?.name ?? '',
      },
    ],
    [menuSourceValue, data.businessQuestions.wantsMenuPhotoShoot, data.documents.businessLogo, fieldErrors, yes, no, t],
  );

  // Tax and bank details are the fields partners most often need to correct
  // after the OCR pre-fill, so they stay editable in place instead of sending
  // the user back through the wizard.
  const taxItems: ReviewItem[] = useMemo(
    () => [
      {
        label: t('taxLegal.taxId') as string,
        value: data.taxLegalInfo.taxId,
        edit: isSubmitted
          ? undefined
          : {
              type: 'number' as const,
              value: data.taxLegalInfo.taxId,
              onChange: (v: string) => updateSection('taxLegalInfo', { taxId: v }),
              error: fieldErrors['tax.taxId'],
              placeholder: t('taxLegal.taxIdPlaceholder') as string,
            },
      },
      {
        label: t('taxLegal.officialBusinessName') as string,
        value: data.taxLegalInfo.officialBusinessName,
        edit: isSubmitted
          ? undefined
          : {
              type: 'text' as const,
              value: data.taxLegalInfo.officialBusinessName,
              onChange: (v: string) =>
                updateSection('taxLegalInfo', { officialBusinessName: v }),
              error: fieldErrors['tax.officialBusinessName'],
              placeholder: t('taxLegal.officialBusinessNamePlaceholder') as string,
            },
      },
    ],
    [data.taxLegalInfo, fieldErrors, isSubmitted, updateSection, t],
  );

  const bankItems: ReviewItem[] = useMemo(
    () => [
      {
        label: t('bankAccount.bankName') as string,
        value: data.bankAccountInfo.bankName,
        edit: isSubmitted
          ? undefined
          : {
              type: 'autocomplete' as const,
              value: data.bankAccountInfo.bankName,
              onChange: (v: string) => updateSection('bankAccountInfo', { bankName: v }),
              error: fieldErrors['bank.bankName'],
              options: bankOptions,
              placeholder: t('bankAccount.bankNamePlaceholder') as string,
            },
      },
      {
        label: t('bankAccount.bankBranchNumber') as string,
        value: data.bankAccountInfo.bankBranchNumber,
        edit: isSubmitted
          ? undefined
          : {
              type: 'number' as const,
              value: data.bankAccountInfo.bankBranchNumber,
              onChange: (v: string) =>
                updateSection('bankAccountInfo', { bankBranchNumber: v }),
              error: fieldErrors['bank.bankBranchNumber'],
              placeholder: t('bankAccount.bankBranchNumberPlaceholder') as string,
            },
      },
      {
        label: t('bankAccount.bankAccountNumber') as string,
        value: data.bankAccountInfo.bankAccountNumber,
        edit: isSubmitted
          ? undefined
          : {
              type: 'number' as const,
              value: data.bankAccountInfo.bankAccountNumber,
              onChange: (v: string) =>
                updateSection('bankAccountInfo', { bankAccountNumber: v }),
              error: fieldErrors['bank.bankAccountNumber'],
              placeholder: t('bankAccount.bankAccountNumberPlaceholder') as string,
            },
      },
      {
        label: t('bankAccount.bankAccountOwnerName') as string,
        value: data.bankAccountInfo.bankAccountOwnerName,
        edit: isSubmitted
          ? undefined
          : {
              type: 'text' as const,
              value: data.bankAccountInfo.bankAccountOwnerName,
              onChange: (v: string) =>
                updateSection('bankAccountInfo', { bankAccountOwnerName: v }),
              error: fieldErrors['bank.bankAccountOwnerName'],
              placeholder: t('bankAccount.bankAccountOwnerNamePlaceholder') as string,
            },
      },
      {
        label: t('bankAccount.bankAccountOwnerId') as string,
        value: data.bankAccountInfo.bankAccountOwnerId,
        edit: isSubmitted
          ? undefined
          : {
              type: 'number' as const,
              value: data.bankAccountInfo.bankAccountOwnerId,
              onChange: (v: string) =>
                updateSection('bankAccountInfo', { bankAccountOwnerId: v }),
              error: fieldErrors['bank.bankAccountOwnerId'],
              placeholder: t('bankAccount.bankAccountOwnerIdPlaceholder') as string,
            },
      },
    ],
    [data.bankAccountInfo, bankOptions, fieldErrors, isSubmitted, updateSection, t],
  );

  const repItems: ReviewItem[] = useMemo(
    () => [
      { label: t('representative.representativeName') as string, value: data.representativeInfo.representativeName, error: fieldErrors['representative.representativeName'] },
      { label: t('representative.representativePhone') as string, value: data.representativeInfo.representativePhone, error: fieldErrors['representative.representativePhone'] },
      { label: t('representative.representativePosition') as string, value: data.representativeInfo.representativePosition, error: fieldErrors['representative.representativePosition'] },
      { label: t('finalDetails.notes') as string, value: data.finalDetails.notes },
    ],
    [data.representativeInfo, data.finalDetails.notes, fieldErrors, t],
  );

  const docItems: ReviewItem[] = useMemo(() => {
    const keys: { key: DocumentKey; labelKey: string }[] = [
      { key: 'osekCertificate', labelKey: 'documents.osekCertificate' },
      { key: 'taxClearance', labelKey: 'documents.taxClearance' },
      { key: 'bankConfirmation', labelKey: 'documents.bankConfirmation' },
      { key: 'businessLogo', labelKey: 'documents.businessLogo' },
    ];
    return keys.map(({ key, labelKey }) => {
      const file = data.documents[key];
      return {
        label: t(labelKey) as string,
        value: file?.name ?? '',
        preview: file,
        error: fieldErrors[`documents.${key}`],
      } satisfies ReviewItem;
    });
  }, [data.documents, fieldErrors, t]);

  // Schema-driven status: schema "succeeds" → complete. If the section has
  // some data but fails the schema → invalid. Otherwise → incomplete.
  const sectionStatus = useMemo(() => {
    const bi = data.businessInfo;
    const bq = data.businessQuestions;

    const contactHasData =
      !!bi.commercialName ||
      !!bi.ownerName ||
      !!bi.phoneNumber ||
      bi.address.trim().length > 0;
    const businessHasData =
      !!bi.businessCategory ||
      !!bi.storeType ||
      !!bi.businessPhoneNumber ||
      !!bi.email ||
      bq.kosherCertifications.length > 0 ||
      bq.deliveryCompanies.length > 0;
    const menuHasData = bq.menuSource !== null || bq.wantsMenuPhotoShoot !== null;
    const taxHasData = !!(data.taxLegalInfo.taxId || data.taxLegalInfo.officialBusinessName);
    const bankHasData = Object.values(data.bankAccountInfo).some(
      (v) => typeof v === 'string' && v.length > 0,
    );
    const repHasData = Object.values(data.representativeInfo).some(
      (v) => typeof v === 'string' && v.length > 0,
    );

    const computeStatus = (valid: boolean, hasData: boolean): SectionStatus => {
      if (valid) return 'complete';
      if (hasData) return 'invalid';
      return 'incomplete';
    };

    const contactValid = stepContactSchema.safeParse({
      commercialName: bi.commercialName,
      ownerName: bi.ownerName,
      phoneNumber: bi.phoneNumber,
      latitude: bi.latitude,
      longitude: bi.longitude,
    }).success;
    const businessValid = stepBusinessSchema.safeParse({
      businessCategory: bi.businessCategory,
      storeType: bi.storeType,
      storeTypeOther: bq.storeTypeOther,
      location: bi.location,
      address: bi.address,
      kosherCertifications: bq.kosherCertifications,
      sensitivities: bq.sensitivities,
      deliveryCompanies: bq.deliveryCompanies,
      otherDeliveryCompanies: bq.otherDeliveryCompanies,
      businessPhoneNumber: bi.businessPhoneNumber,
      email: bi.email,
    }).success;
    const menuValid = stepMenuSchema.safeParse({
      menuSource: bq.menuSource,
      menuWebsiteUrl: bq.menuWebsiteUrl,
      menuFile: bq.menuFile,
      posType: bq.posType,
      wantsMenuPhotoShoot: bq.wantsMenuPhotoShoot,
    }).success;
    const taxValid = taxLegalSchema.safeParse(data.taxLegalInfo).success;
    const bankValid = bankAccountSchema.safeParse(data.bankAccountInfo).success;
    const repValid = representativeSchema.safeParse({
      representativeName: data.representativeInfo.representativeName,
      representativePhone: data.representativeInfo.representativePhone,
      representativePosition: data.representativeInfo.representativePosition,
    }).success;
    // Logo is optional — only the three core documents are required.
    const docsValid =
      !!data.documents.osekCertificate &&
      !!data.documents.taxClearance &&
      !!data.documents.bankConfirmation;

    return {
      contact: computeStatus(contactValid, contactHasData),
      business: computeStatus(businessValid, businessHasData),
      tax: computeStatus(taxValid, taxHasData),
      bank: computeStatus(bankValid, bankHasData),
      representative: computeStatus(repValid, repHasData),
      menu: computeStatus(menuValid, menuHasData),
      documents: docsValid ? ('complete' as const) : ('incomplete' as const),
    };
  }, [data]);

  const reviewStatus = (key: (typeof SECTION_KEYS)[number]): SectionStatus =>
    validationIssues.some((issue) => issue.section === key)
      ? 'invalid'
      : sectionStatus[key];

  // Build the full list of uploaded files for the lightbox so the user can
  // arrow through them once a preview is opened.
  const allFiles: FilePreviewItem[] = useMemo(() => {
    const items: FilePreviewItem[] = [];
    docItems.forEach((d) => {
      if (d.preview) items.push({ label: d.label, file: d.preview });
    });
    return items;
  }, [docItems]);

  const handlePreview = (file: FileMeta, item: ReviewItem) => {
    const idx = allFiles.findIndex((f) => f.file.url === file.url);
    setPreviewItems(allFiles);
    setPreviewIndex(idx >= 0 ? idx : 0);
    void item;
    setPreviewOpen(true);
  };

  // Linear flow: each section maps to its form step. Documents are uploaded
  // inline across the steps; editing them jumps to the Financial step which
  // holds the Osek + tax-clearance certificates.
  const sectionStepIndex: Record<string, number> = {
    contact: 2,
    business: 3,
    tax: 4,
    bank: 4,
    representative: 5,
    menu: 6,
    documents: 4,
  };

  const handleEdit = (step: number, sectionKey: string) => {
    if (isSubmitted) return;
    void sectionKey;
    setStep(step);
  };

  const collectValidationIssues = (): ReviewValidationIssue[] => {
    const issues: ReviewValidationIssue[] = [];
    const addSchemaIssues = (
      section: ReviewSectionKey,
      schemaIssues: ZodIssue[],
      labels: Record<string, string>,
    ) => {
      schemaIssues.forEach((issue) => {
        const field = String(issue.path[0] ?? '');
        const key = `${section}.${field}`;
        if (!field || issues.some((existing) => existing.key === key)) return;
        issues.push({
          key,
          section,
          label: labels[field] ?? field,
          message: issue.message,
        });
      });
    };

    const contactResult = stepContactSchema.safeParse({
      commercialName: data.businessInfo.commercialName,
      ownerName: data.businessInfo.ownerName,
      phoneNumber: data.businessInfo.phoneNumber,
      latitude: data.businessInfo.latitude,
      longitude: data.businessInfo.longitude,
    });
    if (!contactResult.success) {
      addSchemaIssues('contact', contactResult.error.issues, {
        commercialName: t('businessInfo.commercialName') as string,
        ownerName: t('businessInfo.contactPerson') as string,
        phoneNumber: t('businessInfo.phoneNumber') as string,
        latitude: t('businessInfo.location') as string,
      });
    }

    const businessResult = stepBusinessSchema.safeParse({
      businessCategory: data.businessInfo.businessCategory,
      storeType: data.businessInfo.storeType,
      storeTypeOther: data.businessQuestions.storeTypeOther,
      location: data.businessInfo.location,
      address: data.businessInfo.address,
      kosherCertifications: data.businessQuestions.kosherCertifications,
      sensitivities: data.businessQuestions.sensitivities,
      deliveryCompanies: data.businessQuestions.deliveryCompanies,
      otherDeliveryCompanies: data.businessQuestions.otherDeliveryCompanies,
      businessPhoneNumber: data.businessInfo.businessPhoneNumber,
      email: data.businessInfo.email,
    });
    if (!businessResult.success) {
      addSchemaIssues('business', businessResult.error.issues, {
        businessCategory: t('businessCategory.question') as string,
        storeType: t('businessInfo.storeType') as string,
        storeTypeOther: t('businessInfo.storeTypeOther') as string,
        location: t('businessInfo.location') as string,
        otherDeliveryCompanies: t('businessQuestions.otherDeliveryCompaniesLabel') as string,
        businessPhoneNumber: t('businessInfo.businessPhoneNumber') as string,
        email: t('businessInfo.email') as string,
      });
    }

    const taxResult = taxLegalSchema.safeParse(data.taxLegalInfo);
    if (!taxResult.success) {
      addSchemaIssues('tax', taxResult.error.issues, {
        taxId: t('taxLegal.taxId') as string,
        officialBusinessName: t('taxLegal.officialBusinessName') as string,
      });
    }

    const bankResult = bankAccountSchema.safeParse(data.bankAccountInfo);
    if (!bankResult.success) {
      addSchemaIssues('bank', bankResult.error.issues, {
        bankName: t('bankAccount.bankName') as string,
        bankBranchNumber: t('bankAccount.bankBranchNumber') as string,
        bankAccountNumber: t('bankAccount.bankAccountNumber') as string,
        bankAccountOwnerName: t('bankAccount.bankAccountOwnerName') as string,
        bankAccountOwnerId: t('bankAccount.bankAccountOwnerId') as string,
      });
    }

    const representativeResult = representativeSchema.safeParse(data.representativeInfo);
    if (!representativeResult.success) {
      addSchemaIssues('representative', representativeResult.error.issues, {
        representativeName: t('representative.representativeName') as string,
        representativePhone: t('representative.representativePhone') as string,
        representativePosition: t('representative.representativePosition') as string,
      });
    }

    const menuResult = stepMenuSchema.safeParse(data.businessQuestions);
    if (!menuResult.success) {
      addSchemaIssues('menu', menuResult.error.issues, {
        menuSource: t('businessQuestions.menuSource.question') as string,
        menuWebsiteUrl: t('businessQuestions.menuSource.websiteLabel') as string,
        menuFile: t('businessQuestions.menuFileUploadLabel') as string,
        posType: t('businessQuestions.menuSource.posSelectLabel') as string,
        wantsMenuPhotoShoot: t('businessQuestions.wantsMenuPhotoShoot') as string,
      });
    }

    const hoursResult = hoursScheduleSchema.safeParse({
      workingDays: data.businessInfo.workingDays,
      openingHours: data.finalDetails.openingHours,
    });
    if (!hoursResult.success) {
      addSchemaIssues('hours', hoursResult.error.issues, {
        workingDays: t('workingDays.label') as string,
        openingHours: t('businessInfo.openingHours') as string,
      });
    }

    ([
      ['osekCertificate', 'documents.osekCertificate'],
      ['taxClearance', 'documents.taxClearance'],
      ['bankConfirmation', 'documents.bankConfirmation'],
    ] as const).forEach(([key, labelKey]) => {
      if (!data.documents[key]) {
        issues.push({
          key: `documents.${key}`,
          section: 'documents',
          label: t(labelKey) as string,
          message: 'documentRequired',
        });
      }
    });

    return issues;
  };

  const showValidationIssues = (issues: ReviewValidationIssue[]) => {
    setValidationIssues(issues);
    setShowHoursErrors(issues.some((issue) => issue.section === 'hours'));
    setSubmitError(
      issues.length > 0 ? (t('review.errors.fieldsNeedAttention') as string) : undefined,
    );
    afterPaint(() => {
      scrollToFirstInvalidField(null);
    });
  };

  const handleSubmit = async () => {
    if (isSubmitted) return;
    if (!confirmed) {
      setError('mustAccept');
      afterPaint(() => {
        scrollToFirstInvalidField(null);
      });
      return;
    }
    const issues = collectValidationIssues();
    if (issues.length > 0) {
      showValidationIssues(issues);
      return;
    }
    setError(undefined);
    setSubmitError(undefined);
    setValidationIssues([]);
    setShowHoursErrors(false);

    // Guard: an authorized dealer's osek number (אישור עוסק מורשה) must match
    // the bank account owner ת.ז. (אישור ניהול חשבון). A company ח.פ. (prefix 51)
    // is a different legal entity and is not compared to the owner's personal ID.
    const osekNumber = String(data.taxLegalInfo.taxId ?? '').replace(/\D/g, '');
    const bankOwnerId = String(
      data.bankAccountInfo.bankAccountOwnerId ?? '',
    ).replace(/\D/g, '');
    if (
      osekNumber &&
      bankOwnerId &&
      osekNumber !== bankOwnerId &&
      !isCompanyTaxId(osekNumber)
    ) {
      showValidationIssues([
        {
          key: 'tax.taxId',
          section: 'tax',
          label: t('taxLegal.taxId') as string,
          message: 'idMismatch',
        },
        {
          key: 'bank.bankAccountOwnerId',
          section: 'bank',
          label: t('bankAccount.bankAccountOwnerId') as string,
          message: 'idMismatch',
        },
      ]);
      setSubmitError(t('review.errors.idMismatch') as string);
      return;
    }

    setSubmitting(true);
    try {
      // Guard against re-registration: if the partner's email, phone, or tax
      // ID is already present in the submissions table, surface the "already
      // registered" modal instead of creating a duplicate record.
      const dup = await checkDuplicate({
        email: data.businessInfo.email,
        phone: data.businessInfo.phoneNumber,
        taxId: data.taxLegalInfo.taxId,
      });
      if (dup.exists) {
        setAlreadyRegisteredOpen(true);
        setSubmitting(false);
        return;
      }

      const result = await submitOnboarding(data);

      setMetadata({
        submissionId: result.submissionId,
        status: 'Submitted',
      });

      const failed = Object.entries(result.integrations)
        .filter(([, v]) => !v.ok)
        .map(([k]) => k);
      if (failed.length > 0) {
        logger.warn('[HAAT Partner Connect] partial integration failure', failed, result.integrations);
        toast.warn(t('review.toast.integrationsFailed', { failed: failed.join(', ') }) as string);
      }

      goNext();
    } catch (err) {
      // Backend enforces the same guards authoritatively; map its coded
      // responses back to the matching UX (409 duplicate, 422 ID mismatch).
      const response = (err as { response?: { status?: number; data?: { code?: string } } })
        .response;
      const code = response?.data?.code;
      if (response?.status === 409 || code === 'DUPLICATE_REGISTRATION') {
        setAlreadyRegisteredOpen(true);
        return;
      }
      if (response?.status === 422 || code === 'ID_MISMATCH') {
        showValidationIssues([
          {
            key: 'tax.taxId',
            section: 'tax',
            label: t('taxLegal.taxId') as string,
            message: 'idMismatch',
          },
          {
            key: 'bank.bankAccountOwnerId',
            section: 'bank',
            label: t('bankAccount.bankAccountOwnerId') as string,
            message: 'idMismatch',
          },
        ]);
        setSubmitError(t('review.errors.idMismatch') as string);
        return;
      }
      logger.error('[HAAT Partner Connect] submission failed', err);
      toast.error(t('review.toast.submitFailed') as string);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className="flex flex-col gap-5"
    >
      <header className="card p-5 sm:p-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{t('review.title')}</h2>
        <p className="mt-1.5 text-sm text-gray-500 leading-relaxed">{t('review.subtitle')}</p>
      </header>

      {!isSubmitted && (
        <ReviewHoursCard
          workingDays={data.businessInfo.workingDays}
          openingHours={data.finalDetails.openingHours}
          showErrors={showHoursErrors}
          onWorkingDaysChange={(days, openingHours) => {
            updateSection('businessInfo', { workingDays: days });
            updateSection('finalDetails', { openingHours });
          }}
          onOpeningHoursChange={(openingHours) =>
            updateSection('finalDetails', { openingHours })
          }
        />
      )}

      {isSubmitted && (
        <div
          role="status"
          className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 sm:p-5 flex items-start gap-3"
        >
          <span className="w-9 h-9 rounded-xl bg-white text-emerald-600 flex items-center justify-center flex-shrink-0">
            <CheckCircle2 size={20} />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-emerald-900">
              {t('review.submitted.banner', { defaultValue: 'Your registration has been submitted.' })}
            </p>
            <p className="text-xs text-emerald-700 mt-0.5">
              {t('review.submitted.contactSupport', {
                defaultValue: 'To edit any details please contact HAAT support.',
              })}
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-4">
        <ReviewSection
          title={t('review.sectionContact')}
          icon={Store}
          anchorId={sectionAnchor('contact')}
          onEdit={() => handleEdit(sectionStepIndex.contact, 'contact')}
          readOnly={isSubmitted}
          items={contactItems}
          status={reviewStatus('contact')}
        />
        <ReviewSection
          title={t('review.sectionBusiness')}
          icon={Building2}
          anchorId={sectionAnchor('business')}
          onEdit={() => handleEdit(sectionStepIndex.business, 'business')}
          readOnly={isSubmitted}
          items={businessItems}
          status={reviewStatus('business')}
        />
        <ReviewSection
          title={t('review.sectionTax')}
          icon={Scale}
          anchorId={sectionAnchor('tax')}
          readOnly={isSubmitted}
          items={taxItems}
          status={reviewStatus('tax')}
        />
        <ReviewSection
          title={t('review.sectionBank')}
          icon={Banknote}
          anchorId={sectionAnchor('bank')}
          readOnly={isSubmitted}
          items={bankItems}
          status={reviewStatus('bank')}
        />
        <ReviewSection
          title={t('review.sectionRepresentative')}
          icon={UserSquare2}
          anchorId={sectionAnchor('representative')}
          onEdit={() => handleEdit(sectionStepIndex.representative, 'representative')}
          readOnly={isSubmitted}
          items={repItems}
          status={reviewStatus('representative')}
        />
        <ReviewSection
          title={t('review.sectionMenu')}
          icon={UtensilsCrossed}
          anchorId={sectionAnchor('menu')}
          onEdit={() => handleEdit(sectionStepIndex.menu, 'menu')}
          readOnly={isSubmitted}
          items={menuItems}
          status={reviewStatus('menu')}
        />
        <ReviewSection
          title={t('review.sectionDocuments')}
          icon={FileCheck2}
          anchorId={sectionAnchor('documents')}
          onEdit={() => handleEdit(sectionStepIndex.documents, 'documents')}
          readOnly={isSubmitted}
          items={docItems}
          status={reviewStatus('documents')}
          onPreview={handlePreview}
        />
      </div>

      {!isSubmitted && (
        <div className="sticky bottom-0 -mx-4 sm:-mx-6 px-4 sm:px-6 pb-4 pt-3 bg-gradient-to-t from-gray-50 via-gray-50/95 to-gray-50/0">
          <div className="card p-4 sm:p-5 shadow-soft border border-gray-200">
            <FormCheckbox
              id="confirm-submission"
              checked={confirmed}
              onChange={(v) => {
                setConfirmed(v);
                if (v) setError(undefined);
              }}
              error={error}
              label={t('review.confirmation')}
            />
            {submitError && (
              <p className="mt-2 text-xs font-medium text-red-600" role="alert">
                {submitError}
              </p>
            )}
            {validationIssues.length > 0 && (
              <ul className="mt-2 list-disc ps-5 text-xs text-red-700">
                {validationIssues.map((issue) => (
                  <li key={issue.key}>
                    <strong>{issue.label}:</strong>{' '}
                    {t(`validation.${issue.message}`, { defaultValue: issue.message })}
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-3 flex flex-col sm:flex-row items-stretch sm:items-center sm:justify-between gap-3 pt-3 border-t border-gray-100">
              <button
                type="button"
                onClick={goBack}
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
              >
                {dir === 'rtl' ? <ArrowRight size={16} /> : <ArrowLeft size={16} />}
                {t('common.back')}
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="btn-haat inline-flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <span className="loading-spinner h-4 w-4 border-white/40 border-t-white" />
                    <span>{t('review.submitting')}</span>
                  </>
                ) : (
                  <>
                    <span>{t('nav.submit')}</span>
                    <NextIcon size={16} />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {isSubmitted && (
        <div className="card p-4 sm:p-5 border border-gray-200 flex items-center justify-center gap-2 text-sm text-gray-600">
          <Lock size={14} />
          <span>
            {t('review.submitted.locked', {
              defaultValue: 'Form locked after submission.',
            })}
          </span>
        </div>
      )}

      <FilePreviewModal
        open={previewOpen}
        items={previewItems}
        initialIndex={previewIndex}
        onClose={() => setPreviewOpen(false)}
      />

      <AlreadyRegisteredModal
        open={alreadyRegisteredOpen}
        onClose={() => setAlreadyRegisteredOpen(false)}
      />
    </motion.section>
  );
};

export default Step8Review;
